import React, { useState } from 'react';
import BottomModal, { BottomModalItem, BottomModalSection } from '@/components/BottomModal';
import WorkflowButton from './WorkflowButton';

interface WorkflowListItem {
  id: string;
  title: string;
  description?: string;
  nextNodeId?: string;
  contextMessageId?: string; // For threading/replying to specific messages
}

interface WorkflowListSection {
  title: string;
  items: WorkflowListItem[];
}

interface WorkflowListProps {
  header?: string;
  body?: string;
  footer?: string;
  buttonText?: string;
  sections: WorkflowListSection[];
  onItemSelect?: (itemId: string, itemTitle?: string, contextMessageId?: string) => void;
  disabled?: boolean;
  className?: string;
  containedModal?: boolean; // New prop to control modal positioning
}

const WorkflowList: React.FC<WorkflowListProps> = ({
  header,
  body,
  footer,
  buttonText = 'Select',
  sections,
  onItemSelect,
  disabled = false,
  className = '',
  containedModal = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WorkflowListItem | null>(null);

  const handleItemClick = (item: BottomModalItem) => {
    if (!disabled) {
      setSelectedItem(item as WorkflowListItem);
      setIsModalOpen(false);
      if (onItemSelect) {
        onItemSelect(item.id, item.title, item.contextMessageId);
      }
    }
  };

  const toggleModal = () => {
    if (!disabled) {
      setIsModalOpen(!isModalOpen);
    }
  };

  // Convert sections to BottomModal format
  const modalSections: BottomModalSection[] = sections.map(section => ({
    title: section.title,
    items: section.items.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      contextMessageId: item.contextMessageId // Pass contextMessageId for threading
    }))
  }));

  return (
    <div className={`workflow-list ${className}`}>
      {/* Header */}
      {header && (
        <div className="font-semibold text-gray-800 dark:text-white p-3">
          {header}
        </div>
      )}

      {/* Body */}
      {body && (
        <div className="text-gray-600 dark:text-gray-300 px-3 pb-3">
          {body}
        </div>
      )}

      {/* List Button */}
      <div className="">
        <WorkflowButton
          buttons={[{
            id: 'open-modal',
            label: selectedItem ? selectedItem.title : buttonText,
            nextNodeId: undefined
          }]}
          onButtonClick={() => toggleModal()}
          disabled={disabled}
          className="w-full"
        />
      </div>

      {/* Bottom Modal */}
      {containedModal ? (
        // Contained modal for use within chat widget
        <div className={`absolute inset-0 z-50 flex items-end justify-center overflow-hidden ${isModalOpen ? 'block' : 'hidden'}`}>
          <div className="w-full bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-y-auto max-h-[60vh] animate-in slide-in-from-bottom duration-300">
            {/* Modal Header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {buttonText}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div>
              {/* Sections */}
              {sections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  {/* Section Title */}
                  {section.title && (
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50/50 dark:bg-gray-900/50 border-y border-gray-100 dark:border-gray-700 first:border-t-0">
                      {section.title}
                    </div>
                  )}

                  {/* Section Items */}
                  {section.items.map((item) => {
                    const isSelected = selectedItem?.id === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className={`w-full px-4 py-3 text-left transition-all flex items-center gap-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${isSelected
                          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold"
                          : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white"
                          }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{item.title}</div>
                          {item.description && (
                            <div className="text-xs text-gray-400 dark:text-gray-400 truncate">
                              {item.description}
                            </div>
                          )}
                        </div>

                        {/* Radio Button */}
                        <div className="relative">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                              }`}
                          >
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        // Default BottomModal for full screen use
        <BottomModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onItemSelect={handleItemClick}
          title={buttonText}
          sections={modalSections}
          selectedItem={selectedItem}
        />
      )}

      {/* Footer */}
      {footer && (
        <div className="text-xs text-gray-500 dark:text-gray-300 p-3 bg-gray-50 dark:bg-gray-600/60">
          {footer}
        </div>
      )}
    </div>
  );
};

export default WorkflowList;
